import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState } from 'react';
import Spinner from '../../../shared/spinner';
import InputMask from 'react-input-mask';
import { format, parse } from 'date-fns';
import { Empregador } from '../../../shared/model/empregador';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { HistoricoFinanceiro } from '../../../shared/model/historicoFinanceiro';
import { RelatoriosDTO } from '../../../shared/dto/relatoriosDTO';
import EmpregadorService from '../../../services/empregadorService';
import HistoricoFinanceiroService from '../../../services/historicoFinanceiroService';
import CustomAlert from '../../../shared/customAlert';
import RelatorioService from '../../../services/relatorioService';
import RelatoriosAgendadosService from '../../../services/relatoriosAgendadosService';
import { AgendarRelatorioDTO } from '../../../shared/dto/agendarRelatorioDTO';

function MovimentacaoFinanceiraComponent() {
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAgendamentoModal, setShowAgendamentoModal] = useState(false);
  const [errorRelatorio, setErrorRelatorio] = useState<string | null>(null);
  const [totalLinhasRelatorio, setTotalLinhasRelatorio] = useState<number | null>(null);

  const [dtBase1, setDtBase1] = useState(format(new Date(), 'dd/MM/yyyy'));
  const [dtBase2, setDtBase2] = useState(format(new Date(), 'dd/MM/yyyy'));
  const [agendamentoData, setAgendamentoData] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // formato: "YYYY-MM-DD"
  });

  const [availableHistoricosFinanceiro, setAvailableHistoricosFinanceiro] = useState<HistoricoFinanceiro[]>([]);
  const [selectedHistoricosFinanceiro, setSelectedHistoricosFinanceiro] = useState<HistoricoFinanceiro[]>([]);
  const [selectedToMoveHistoricosFinanceiro, setSelectedToMoveHistoricosFinanceiro] = useState<HistoricoFinanceiro[]>([]);
  const [selectedFromSelectedHistoricosFinanceiro, setSelectedFromSelectedHistoricosFinanceiro] = useState<HistoricoFinanceiro[]>([]);

  const [availableEmpregadores, setAvailableEmpregadores] = useState<Empregador[]>([]);
  const [selectedEmpregadores, setSelectedEmpregadores] = useState<Empregador[]>([]);
  const [selectedToMoveEmpregadores, setSelectedToMoveEmpregadores] = useState<Empregador[]>([]);
  const [selectedFromSelectedEmpregadores, setSelectedFromSelectedEmpregadores] = useState<Empregador[]>([]);

  const relatorioService: RelatorioService = new RelatorioService();
  const empregadorService: EmpregadorService = new EmpregadorService();
  const historicoFinanceiroService: HistoricoFinanceiroService = new HistoricoFinanceiroService();
  const relatoriosAgendadosService = new RelatoriosAgendadosService();

  const listarHistoricosFinanceiro = async () => {
    setLoading(true);
    try {
      const response = await historicoFinanceiroService.listarTodasHistoricoFinanceiros();
      setAvailableHistoricosFinanceiro(response.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const listarEmpregadores = async () => {
    setLoading(true);
    try {
      const response = await empregadorService.listarTodosEmpregadoresAtivos();
      setAvailableEmpregadores(response.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const gerarRelatorio = async () => {
    if (selectedEmpregadores.length <= 0) return setAlert({ message: 'Selecione ao menos um Empregador', type: 'warning' });
    if (selectedHistoricosFinanceiro.length <= 0) return setAlert({ message: 'Selecione ao menos uma Movimentação Financeira', type: 'warning' });
    if (dtBase1 === '' || dtBase2 === '') return setAlert({ message: 'Preencha os campos de datas!', type: 'warning' });
    if (dtBase1 !== '' && dtBase2 !== '' && parse(dtBase1, "dd/MM/yyyy", new Date()) > parse(dtBase2, "dd/MM/yyyy", new Date())) return setAlert({ message: 'Data inicial deve ser inferior a data final', type: 'warning' });
    
    setLoading(true);
    try {
      const dto: RelatoriosDTO = new RelatoriosDTO();
      dto.empregadores = selectedEmpregadores;
      dto.dtBase1 = parse(dtBase1, "dd/MM/yyyy", new Date());
      dto.dtBase2 = parse(dtBase2, "dd/MM/yyyy", new Date());
      dto.historicosFinanceiros = selectedHistoricosFinanceiro;

      const response = await relatorioService.gerarRelatorioDeMovimentacaoFinanceira(dto);
      const contentType = response.headers['content-type'];

      if (contentType === 'text/plain') {
        const errorText = await response.data.text();
        setAlert({ message: errorText, type: 'warning' });
      } else {
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        link.setAttribute('download', 'Relatorio_Movimentação_Financeira.xlsx');

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      setLoading(false);
    } catch (err: any) {
      setLoading(false);

      if (err.response && err.response.status === 500 && err.response.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const parsed = JSON.parse(errorText);

          if (
            parsed &&
            typeof parsed === 'object' &&
            'message' in parsed &&
            'maxAllowed' in parsed &&
            'totalRows' in parsed &&
            parsed.status === 'error'
          ) {
            setErrorRelatorio(parsed.message);
            setTotalLinhasRelatorio(parsed.totalRows); // captura o totalRows
          } else {
            setErrorRelatorio(errorText);
            setTotalLinhasRelatorio(null); // limpa se não encontrou info
          }
        } catch (parseError) {
          setErrorRelatorio('Erro desconhecido ao gerar relatório.');
          setTotalLinhasRelatorio(null);
          console.error('Erro ao fazer parse do JSON de erro:', parseError);
        }

        setShowAgendamentoModal(true);
      } else if (err.response && err.response !== 'AXIOS_TIMEOUT') {
        setAlert({ message: 'Ocorreu um erro ao gerar o relatório', type: 'error' });
      }

      console.error(err);
    }

  };

  const agendarRelatorio = async () => {
    const token = "Bearer ey..."; // Substituir por token real
    const userId = localStorage.getItem("id") ?? ""; // Substituir por userId real

    if (!agendamentoData) {
      setAlert({ message: "Por favor, selecione a data do agendamento.", type: "warning" });
      return;
    }

    // Validação de data passada
    const selectedDate = new Date(agendamentoData);
    const now = new Date();

    // Zera o horário de AMBAS as datas para comparar apenas a parte da data
    const selectedDateOnly = new Date(selectedDate);
    selectedDateOnly.setHours(0, 0, 0, 0);

    const nowOnly = new Date(now);
    nowOnly.setHours(0, 0, 0, 0);

    // Agora compara as datas com horário zerado
    if (selectedDateOnly < nowOnly) {
      setAlert({ message: "A data de agendamento não pode ser anterior a hoje.", type: "warning" });
      return;
    }

    const parametros = {
      dtBase1: format(parse(dtBase1, "dd/MM/yyyy", new Date()), "yyyy-MM-dd"),
      dtBase2: format(parse(dtBase2, "dd/MM/yyyy", new Date()), "yyyy-MM-dd"),
      empregadorIds: selectedEmpregadores.map(e => e.id),
      historicoFinanceiroIds: selectedHistoricosFinanceiro.map(h => h.id)
    };

    const dto: AgendarRelatorioDTO = {
      userId,
      tipoRelatorio: "MOVIMENTACAO-FINANCEIRA",
      parametros: JSON.stringify(parametros),
      qtdeLinhas: totalLinhasRelatorio ?? 0,
      programado: selectedDate.toISOString()
    };

    try {
      const response = await relatoriosAgendadosService.solicitarRelatorio(dto);

      if (response.status === 200) {
        setAlert({ message: "Relatório agendado com sucesso!", type: "success" });
      } else {
        setAlert({ message: "Erro ao agendar relatório.", type: "error" });
      }
    } catch (err: any) {
      const msg = err.response?.data || "Erro inesperado ao agendar relatório.";
      setAlert({ message: msg, type: "error" });
      console.error(err);
    } finally {
      setShowAgendamentoModal(false);
      setErrorRelatorio(null);
    }
  };

  useEffect(() => {
    listarHistoricosFinanceiro();
    listarEmpregadores();
  }, []);

  const moveToSelectedHistoricosFinanceiro = () => {
    const newSelected = [...selectedHistoricosFinanceiro, ...selectedToMoveHistoricosFinanceiro];
    const newAvailable = availableHistoricosFinanceiro.filter(
      (historicoFinanceiro) => !selectedToMoveHistoricosFinanceiro.includes(historicoFinanceiro)
    );

    setAvailableHistoricosFinanceiro(newAvailable);
    setSelectedHistoricosFinanceiro(newSelected);
    setSelectedToMoveHistoricosFinanceiro([]);
  };

  const moveToAvailableHistoricosFinanceiro = () => {
    const newAvailable = [...availableHistoricosFinanceiro, ...selectedFromSelectedHistoricosFinanceiro];
    const newSelected = selectedHistoricosFinanceiro.filter(
      (historicoFinanceiro) => !selectedFromSelectedHistoricosFinanceiro.includes(historicoFinanceiro)
    );

    setAvailableHistoricosFinanceiro(newAvailable);
    setSelectedHistoricosFinanceiro(newSelected);
    setSelectedFromSelectedHistoricosFinanceiro([]);
  };

  const moveAllToSelectedHistoricosFinanceiro = () => {
    const newSelected = [...selectedHistoricosFinanceiro, ...availableHistoricosFinanceiro];
    setSelectedHistoricosFinanceiro(newSelected);
    setAvailableHistoricosFinanceiro([]);
  };

  const moveAllToAvailableHistoricosFinanceiro = () => {
    const newAvailable = [...availableHistoricosFinanceiro, ...selectedHistoricosFinanceiro];
    setAvailableHistoricosFinanceiro(newAvailable);
    setSelectedHistoricosFinanceiro([]);
  };

  const moveToSelectedEmpregadores = () => {
    const newSelected = [...selectedEmpregadores, ...selectedToMoveEmpregadores];
    const newAvailable = availableEmpregadores.filter(
      (empregador) => !selectedToMoveEmpregadores.includes(empregador)
    );

    setAvailableEmpregadores(newAvailable);
    setSelectedEmpregadores(newSelected);
    setSelectedToMoveEmpregadores([]);
  };

  const moveToAvailableEmpregadores = () => {
    const newAvailable = [...availableEmpregadores, ...selectedFromSelectedEmpregadores];
    const newSelected = selectedEmpregadores.filter(
      (empregador) => !selectedFromSelectedEmpregadores.includes(empregador)
    );

    setAvailableEmpregadores(newAvailable);
    setSelectedEmpregadores(newSelected);
    setSelectedFromSelectedEmpregadores([]);
  };

  const moveAllToSelectedEmpregadores = () => {
    const newSelected = [...selectedEmpregadores, ...availableEmpregadores];
    setSelectedEmpregadores(newSelected);
    setAvailableEmpregadores([]);
  };

  const moveAllToAvailableEmpregadores = () => {
    const newAvailable = [...availableEmpregadores, ...selectedEmpregadores];
    setAvailableEmpregadores(newAvailable);
    setSelectedEmpregadores([]);
  };

  return (
    <>  
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={()=>setAlert(null)} />)}
      
      {/* Modal de Agendamento */}
      {showAgendamentoModal && (
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Agendar Relatório</h5>
                <button type="button" className="close" aria-label="Fechar" onClick={() => setShowAgendamentoModal(false)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>{errorRelatorio}</p>
                <p>Deseja agendar o relatório com os filtros preenchidos?</p>
                <div className="form-group">
                  <label htmlFor="dataAgendamento"><b>Data do Agendamento:</b></label>
                  <input
                    id="dataAgendamento"
                    type="date"
                    required
                    className="form-control"
                    value={agendamentoData}
                    onChange={(e) => setAgendamentoData(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAgendamentoModal(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={agendarRelatorio}>Agendar</button>
              </div>
            </div>
          </div> 
        </div>
      )}
 
      <div className="position-relative m-md-3">
        {loading ? <Spinner loading={loading} /> : (
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header">
              <strong>Relatório de Movimentação Financeira</strong>
            </div>
            <div className="card-body">
              <div className="" style={{ marginTop: '10px' }}>
                <div className="row">
                  <div className="col-md-2" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                    <label>Período de Data Base <span style={{ color: 'red' }}>*</span></label>
                  </div>
                </div>
                <div className="row" style={{ marginTop: '5px' }}>
                  <div className="col-md-4">
                    <thead>
                      <tr>
                        <th style={{ width: "150px" }}><InputMask required mask="99/99/9999" className="form-control" type="text" value={dtBase1} onChange={(e) => setDtBase1((e.target.value).replace(/_/g, ""))} /></th>
                        <th style={{ width: "15px" }}> a </th>
                        <th style={{ width: "150px" }}><InputMask required mask="99/99/9999" className="form-control" type="text" value={dtBase2} onChange={(e) => setDtBase2((e.target.value).replace(/_/g, ""))} /></th>
                      </tr>
                    </thead>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', marginTop: '10px' }}>
                <div>
                  <h6>Histórico Financeiro Disponíveis</h6>
                  <select
                    multiple
                    value={selectedToMoveHistoricosFinanceiro.map((p) => p.id!.toString())}
                    onChange={(e) => {
                      const options = e.target.options;
                      const selected = [];
                      for (let i = 0; i < options.length; i++) {
                        if (options[i].selected) {
                          const historicoFinanceiro = availableHistoricosFinanceiro.find(p => p.id!.toString() === options[i].value);
                          if (historicoFinanceiro) {
                            selected.push(historicoFinanceiro);
                          }
                        }
                      }
                      setSelectedToMoveHistoricosFinanceiro(selected);
                    }}
                    style={{ width: '575px', height: '250px' }}
                  >
                    {availableHistoricosFinanceiro.map((historicoFinanceiro) => (
                      <option key={historicoFinanceiro.id} value={historicoFinanceiro.id!.toString()}>
                        {historicoFinanceiro.id} - {historicoFinanceiro.nome!} ({historicoFinanceiro.tipoEntradaSaida})
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <button onClick={moveToSelectedHistoricosFinanceiro} style={{ margin: '5px' }}>{'>'}</button>
                  <button onClick={moveAllToSelectedHistoricosFinanceiro} style={{ margin: '5px' }}>{'>>'}</button>
                  <button onClick={moveToAvailableHistoricosFinanceiro} style={{ margin: '5px' }}>{'<'}</button>
                  <button onClick={moveAllToAvailableHistoricosFinanceiro} style={{ margin: '5px' }}>{'<<'}</button>
                </div>
                <div>
                  <h6>Histórico Financeiro Selecionados</h6>
                  <select
                    multiple
                    value={selectedFromSelectedHistoricosFinanceiro.map((p) => p.id!.toString())}
                    onChange={(e) => {
                      const options = e.target.options;
                      const selected = [];
                      for (let i = 0; i < options.length; i++) {
                        if (options[i].selected) {
                          const historicoFinanceiro = selectedHistoricosFinanceiro.find(p => p.id!.toString() === options[i].value);
                          if (historicoFinanceiro) {
                            selected.push(historicoFinanceiro);
                          }
                        }
                      }
                      setSelectedFromSelectedHistoricosFinanceiro(selected);
                    }}
                    style={{ width: '575px', height: '250px' }}
                  >
                    {selectedHistoricosFinanceiro.map((historicoFinanceiro) => (
                      <option key={historicoFinanceiro.id} value={historicoFinanceiro.id!.toString()}>
                        {historicoFinanceiro.id} - {historicoFinanceiro.nome!} ({historicoFinanceiro.tipoEntradaSaida})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', marginTop: '10px' }}>
                <div>
                  <h6>Empregadores Disponíveis </h6>
                  < select
                    multiple
                    value={selectedToMoveEmpregadores.map((e) => e.id!.toString())}
                    onChange={(e) => {
                      const options = e.target.options;
                      const selected = [];
                      for (let i = 0; i < options.length; i++) {
                        if (options[i].selected) {
                          const empregador = availableEmpregadores.find(e => e.id!.toString() === options[i].value);
                          if (empregador) {
                            selected.push(empregador);
                          }
                        }
                      }
                      setSelectedToMoveEmpregadores(selected);
                    }}
                    style={{ width: '575px', height: '250px' }}
                  >
                    {
                      availableEmpregadores.map((empregador) => (
                        <option key={empregador.id} value={empregador.id!.toString()} >
                          {empregador.id} - {empregador.nome!}
                        </option>
                      ))
                    }
                  </select>
                </div>
                < div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <button onClick={moveToSelectedEmpregadores} style={{ margin: '5px' }}> {'>'} </button>
                  < button onClick={moveAllToSelectedEmpregadores} style={{ margin: '5px' }}> {'>>'} </button>
                  < button onClick={moveToAvailableEmpregadores} style={{ margin: '5px' }}> {'<'} </button>
                  < button onClick={moveAllToAvailableEmpregadores} style={{ margin: '5px' }}> {'<<'} </button>
                </div>
                < div >
                  <h6>Empregadores Selecionados </h6>
                  < select
                    multiple
                    value={selectedFromSelectedEmpregadores.map((e) => e.id!.toString())}
                    onChange={(e) => {
                      const options = e.target.options;
                      const selected = [];
                      for (let i = 0; i < options.length; i++) {
                        if (options[i].selected) {
                          const empregador = selectedEmpregadores.find(e => e.id!.toString() === options[i].value);
                          if (empregador) {
                            selected.push(empregador);
                          }
                        }
                      }
                      setSelectedFromSelectedEmpregadores(selected);
                    }}
                    style={{ width: '575px', height: '250px' }}
                  >
                    {
                      selectedEmpregadores.map((empregador) => (
                        <option key={empregador.id} value={empregador.id!.toString()} >
                          {empregador.id} - {empregador.nome!}
                        </option>
                      ))
                    }
                  </select>
                </div>
              </div>

              <div className="row" style={{ marginTop: '20px', height: '50px', maxWidth: '70%' }}>
                <div className="col-md-3">
                  <button className="input-group-btn" id="App-button-cor-principal"
                    style={{
                      width: '80%', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '100%', minWidth: '50px'
                    }} onClick={gerarRelatorio}>
                    <a>
                      <FontAwesomeIcon icon={faPrint} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                    </a>
                    <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Gerar Relatório</strong>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div >
    </>
  );
}

export default MovimentacaoFinanceiraComponent;